import { TrendingDown, TrendingUp } from 'lucide-react';
import { useCallback, useState } from 'react';
import { InstitutionalHolderChartTooltip } from '@/components/InstitutionalHolderChartTooltip';
import { InstitutionalHoldersQuarterPopover } from '@/components/InstitutionalHoldersQuarterPopover';
import type { InstitutionalHoldersFileResponse } from '@/services/api';
import type { FilingPeriodCard, FilingPeriodForChart } from '@/utils/institutionalHoldersChartData';
import {
  institutionalHolderCardIsNegative,
  institutionalHolderCardMetricLabel,
} from '@/utils/institutionalHoldersChartData';
import type { InstitutionalHoldersChartDisplayMode } from '@/utils/institutionalHoldersChartDisplayMode';
import { buildInstitutionalHolderDetailPanel } from '@/utils/institutionalHolderQuarterHistory';
import type { InstitutionalHoldersChartDensityMode } from '@/utils/institutionalHoldersChartDensity';
export {
  INSTITUTIONAL_HOLDERS_COLUMN_WIDTH_PX,
} from '@/utils/institutionalHoldersChartDensity';

/** Height of the strip above the price plot for 13F quarter columns. */
export const INSTITUTIONAL_HOLDERS_RIBBON_HEIGHT_PX = 200;

const RIBBON_LIST_MAX_HEIGHT_PX = INSTITUTIONAL_HOLDERS_RIBBON_HEIGHT_PX - 36;

function barCenterX(
  xScale: (v: string) => number,
  bw: number,
  chartDate: string
): number | null {
  const x0 = xScale(chartDate);
  if (x0 == null || Number.isNaN(Number(x0))) return null;
  return Number(x0) + (bw > 0 ? bw / 2 : 0);
}

/** Plot-inner X for column center (between consecutive quarter-end dividers). */
export function columnCenterPlotX(
  xScale: (v: string) => number,
  bw: number,
  filingPeriods: FilingPeriodForChart[],
  index: number,
  visibleChartStartDate?: string | null,
  /** All quarter-end divider dates on the chart (includes clipped quarters). */
  quarterEndChartDatesAsc?: string[]
): number | null {
  const period = filingPeriods[index];
  if (!period) return null;

  const endDate = period.chartDate;
  let startDate: string;
  if (index > 0) {
    startDate = filingPeriods[index - 1]!.chartDate;
  } else {
    const priorEnds = (quarterEndChartDatesAsc ?? []).filter((d) => d < period.chartDate);
    if (priorEnds.length > 0) {
      startDate = priorEnds[priorEnds.length - 1]!;
    } else {
      const viewportStart = visibleChartStartDate?.trim() || null;
      startDate =
        viewportStart && period.chartStartDate < viewportStart
          ? viewportStart
          : period.chartStartDate;
    }
  }

  const startCx = barCenterX(xScale, bw, startDate);
  const endCx = barCenterX(xScale, bw, endDate);
  if (startCx == null || endCx == null) return null;
  return (startCx + endCx) / 2;
}

type HolderTooltipState = {
  positionKey: string;
  anchorRect: DOMRect;
};

function QuarterPill({ label, compact }: { label: string; compact?: boolean }) {
  return (
    <span
      className={`bg-slate-900 text-white font-light tracking-wide rounded-full whitespace-nowrap ${
        compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-3 py-1 text-xs'
      }`}
    >
      {label}
    </span>
  );
}

function InstitutionCard({
  inst,
  isOpen,
  isHighlighted,
  compact,
  displayMode,
  quarterKey,
  onActivate,
}: {
  inst: FilingPeriodCard;
  isOpen: boolean;
  isHighlighted?: boolean;
  compact: boolean;
  displayMode: InstitutionalHoldersChartDisplayMode;
  quarterKey: string;
  onActivate: (el: HTMLElement) => void;
}) {
  const isOwnership = displayMode === 'ownership';
  const isNegative = !isOwnership && institutionalHolderCardIsNegative(inst);
  const metricLabel = institutionalHolderCardMetricLabel(inst, displayMode);
  const highlightClass = isHighlighted ? 'ring-2 ring-amber-400 border-amber-300' : '';

  if (compact) {
    return (
      <div
        role="button"
        data-inst-holder={`${quarterKey}|${inst.positionKey}`}
        tabIndex={0}
        onClick={(e) => onActivate(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate(e.currentTarget);
          }
        }}
        className={`bg-white border rounded px-1 py-0.5 shadow-sm flex-shrink-0 cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-1 focus-visible:ring-slate-400 ${
          isOwnership ? 'border-slate-200' : isNegative ? 'border-rose-200' : 'border-emerald-200'
        } ${isOpen ? 'ring-1 ring-slate-400 shadow-md' : ''} ${highlightClass}`}
        title={`${inst.name} · ${metricLabel}`}
      >
        <div className="flex items-center gap-0.5 min-w-0">
          <span className="text-[9px] font-medium text-slate-700 truncate min-w-0 flex-1 leading-tight">
            {inst.name}
          </span>
          {!isOwnership &&
            (isNegative ? (
              <TrendingDown className="w-2.5 h-2.5 text-rose-500 flex-shrink-0" />
            ) : (
              <TrendingUp className="w-2.5 h-2.5 text-emerald-600 flex-shrink-0" />
            ))}
        </div>
        <p
          className={`text-[9px] font-light leading-tight truncate ${
            isOwnership ? 'text-slate-600' : isNegative ? 'text-rose-600' : 'text-emerald-700'
          }`}
        >
          {metricLabel}
        </p>
      </div>
    );
  }

  return (
    <div
      role="button"
      data-inst-holder={`${quarterKey}|${inst.positionKey}`}
      tabIndex={0}
      onClick={(e) => onActivate(e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate(e.currentTarget);
        }
      }}
      className={`bg-white border rounded-lg px-2.5 py-1.5 shadow-sm flex-shrink-0 cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
        isOwnership ? 'border-slate-200' : isNegative ? 'border-rose-200' : 'border-emerald-200'
      } ${isOpen ? 'ring-2 ring-slate-400 shadow-md' : ''} ${highlightClass}`}
    >
      <div className="flex items-center justify-between gap-1.5">
        <span className="text-xs font-medium text-slate-700 truncate min-w-0">
          {inst.name}
        </span>
        {!isOwnership &&
          (isNegative ? (
            <TrendingDown className="w-3 h-3 text-rose-500 flex-shrink-0" />
          ) : (
            <TrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0" />
          ))}
      </div>
      <div
        className={`text-xs font-light mt-0.5 ${
          isOwnership ? 'text-slate-600' : isNegative ? 'text-rose-600' : 'text-emerald-700'
        }`}
      >
        {metricLabel}
      </div>
    </div>
  );
}

export function InstitutionalHoldersColumn({
  period,
  maxListHeight = RIBBON_LIST_MAX_HEIGHT_PX,
  holdersPayload,
  stockSymbol,
  displayMode = 'activity',
  searchHighlight = null,
  searchFilterActive = false,
  densityMode = 'full',
  maxInstitutions = Number.POSITIVE_INFINITY,
}: {
  period: FilingPeriodForChart;
  maxListHeight?: number;
  holdersPayload: InstitutionalHoldersFileResponse | null;
  stockSymbol: string;
  displayMode?: InstitutionalHoldersChartDisplayMode;
  searchHighlight?: { quarterKey: string; positionKey: string } | null;
  searchFilterActive?: boolean;
  densityMode?: InstitutionalHoldersChartDensityMode;
  maxInstitutions?: number;
}) {
  const [holderTooltip, setHolderTooltip] = useState<HolderTooltipState | null>(null);
  const [quarterPopoverOpen, setQuarterPopoverOpen] = useState(false);
  const [quarterAnchor, setQuarterAnchor] = useState<DOMRect | null>(null);

  const compact = densityMode === 'compact';
  const minimal = densityMode === 'minimal';
  const totalCount = period.institutions.length;
  const visibleInstitutions = period.institutions.slice(
    0,
    Number.isFinite(maxInstitutions) ? maxInstitutions : totalCount
  );
  const hiddenCount = totalCount - visibleInstitutions.length;

  const toggleHolderTooltip = useCallback((positionKey: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setHolderTooltip((prev) =>
      prev?.positionKey === positionKey ? null : { positionKey, anchorRect: rect }
    );
  }, []);

  const onRibbonInstitutionClick = useCallback(
    (positionKey: string, el: HTMLElement) => {
      toggleHolderTooltip(positionKey, el);
      setQuarterPopoverOpen(false);
    },
    [toggleHolderTooltip]
  );

  const onPopoverInstitutionClick = useCallback(
    (positionKey: string, el: HTMLElement) => {
      toggleHolderTooltip(positionKey, el);
    },
    [toggleHolderTooltip]
  );

  const openQuarterPopover = useCallback((el: HTMLElement) => {
    setHolderTooltip(null);
    setQuarterAnchor(el.getBoundingClientRect());
    setQuarterPopoverOpen((open) => !open);
  }, []);

  const detail =
    holderTooltip && holdersPayload
      ? buildInstitutionalHolderDetailPanel(
          holdersPayload,
          stockSymbol,
          holderTooltip.positionKey,
          period.quarterKey
        )
      : null;

  return (
    <div className="flex h-full w-full flex-col items-center pointer-events-auto overflow-hidden">
      <div className={`flex shrink-0 justify-center ${minimal ? 'mb-1' : 'mb-2'} px-0.5`}>
        <QuarterPill label={period.quarterLabel} compact={minimal || compact} />
      </div>

      {totalCount === 0 && searchFilterActive ? (
        <p className="mt-1 px-1 text-center text-[9px] text-slate-400">No match</p>
      ) : minimal ? (
        <button
          type="button"
          onClick={(e) => openQuarterPopover(e.currentTarget)}
          className="mt-0.5 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-slate-400"
          title={`View ${totalCount} institutional ${displayMode === 'ownership' ? 'holders' : 'movers'}`}
        >
          {totalCount} {displayMode === 'ownership' ? 'holder' : 'mover'}
          {totalCount === 1 ? '' : 's'}
        </button>
      ) : (
        <div
          className="institutional-holders-ribbon-scroll flex w-full flex-col gap-1 overflow-y-auto overflow-x-hidden pb-1 pr-0.5"
          style={{ maxHeight: maxListHeight }}
        >
          {visibleInstitutions.map((inst) => (
            <InstitutionCard
              key={`${period.quarterKey}-${inst.positionKey}`}
              inst={inst}
              quarterKey={period.quarterKey}
              compact={compact}
              displayMode={displayMode}
              isHighlighted={
                searchHighlight?.quarterKey === period.quarterKey &&
                searchHighlight.positionKey === inst.positionKey
              }
              isOpen={holderTooltip?.positionKey === inst.positionKey}
              onActivate={(el) => onRibbonInstitutionClick(inst.positionKey, el)}
            />
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={(e) => openQuarterPopover(e.currentTarget)}
              className="flex-shrink-0 rounded-md border border-dashed border-slate-200 bg-slate-50 px-1.5 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-100"
            >
              +{hiddenCount} more
            </button>
          )}
        </div>
      )}

      {quarterPopoverOpen && quarterAnchor && (
        <InstitutionalHoldersQuarterPopover
          period={period}
          displayMode={displayMode}
          anchorRect={quarterAnchor}
          onClose={() => {
            setQuarterPopoverOpen(false);
            setHolderTooltip(null);
          }}
          selectedPositionKey={holderTooltip?.positionKey ?? null}
          onSelectInstitution={onPopoverInstitutionClick}
        />
      )}

      {holderTooltip && detail && (
        <InstitutionalHolderChartTooltip
          detail={detail}
          anchorRect={holderTooltip.anchorRect}
          onClose={() => setHolderTooltip(null)}
        />
      )}
    </div>
  );
}
